import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Plus, Trash2, Repeat } from "lucide-react";
import { PageContainer } from "@/layout/PageContainer";
import { PageHeader } from "@/ui/PageHeader";
import { KPICard } from "@/ui/KPICard";
import { Card } from "@/ui/Card";
import { Badge } from "@/ui/Badge";
import { Button } from "@/ui/Button";
import { SegmentedFilter, type FilterOption } from "@/ui/SegmentedFilter";
import { DataTable, type Column } from "@/ui/data-table/DataTable";
import { ConfirmDialog } from "@/ui/modals/ConfirmDialog";
import { Modal, Field, fieldInputClass } from "@/ui/modals/Modal";
import { LoadingState } from "@/ui/feedback/states";
import { ApiFallbackBanner } from "@/ui/feedback/ApiFallbackBanner";
import { toast } from "@/stores/toast.store";
import { getErrorMessage } from "@/lib/api-errors";
import { formatDZD } from "@/lib/formatters";
import { useLabels } from "@/lib/labels";
import type { Expense, ExpenseCategory } from "@/types";
import { useExpenses, useCreateExpense, useArchiveExpense } from "./expenses.hooks";
import type { ExpenseInput } from "./expenses.adapter";

type Filter = "all" | ExpenseCategory;

export function ExpensesPage() {
  const { t } = useTranslation();
  const { EXPENSE_CATEGORY } = useLabels();
  const FILTERS: FilterOption<Filter>[] = [
    { value: "all", label: t("expenses.filters.all") },
    ...(Object.keys(EXPENSE_CATEGORY) as ExpenseCategory[]).map((c) => ({ value: c, label: EXPENSE_CATEGORY[c].label })),
  ];
  const { expenses, isLoading, isError, refetch } = useExpenses();
  const createExpense = useCreateExpense();
  const archiveExpense = useArchiveExpense();
  const [filter, setFilter] = useState<Filter>("all");
  const [toDelete, setToDelete] = useState<Expense | undefined>();
  const [addOpen, setAddOpen] = useState(false);

  const visible = useMemo(() => (filter === "all" ? expenses : expenses.filter((e) => e.category === filter)), [expenses, filter]);
  const total = useMemo(() => expenses.reduce((s, e) => s + e.amount, 0), [expenses]);
  const recurrent = useMemo(() => expenses.filter((e) => e.recurrent).reduce((s, e) => s + e.amount, 0), [expenses]);
  const byCategory = (cat: ExpenseCategory) => expenses.filter((e) => e.category === cat).reduce((s, e) => s + e.amount, 0);

  const onCreate = async (input: ExpenseInput) => {
    try {
      await createExpense.mutateAsync(input);
      toast.success("Dépense ajoutée");
      setAddOpen(false);
    } catch (err) {
      toast.error(getErrorMessage(err, "Échec de l'ajout de la dépense"));
    }
  };

  const onArchiveConfirm = async () => {
    if (!toDelete) return;
    try {
      await archiveExpense.mutateAsync(toDelete.id);
      toast.success("Dépense archivée");
    } catch (err) {
      toast.error(getErrorMessage(err, "Échec de l'archivage"));
    } finally {
      setToDelete(undefined);
    }
  };

  const columns: Column<Expense>[] = [
    { key: "desc", header: t("expenses.columns.description"), cell: (e) => <span className="font-semibold text-text">{e.description}</span> },
    { key: "cat", header: t("expenses.columns.category"), cell: (e) => <Badge tone={EXPENSE_CATEGORY[e.category].tone}>{EXPENSE_CATEGORY[e.category].label}</Badge> },
    { key: "amount", header: t("expenses.columns.amount"), align: "right", cell: (e) => <span className="mono">{formatDZD(e.amount)}</span> },
    { key: "date", header: t("expenses.columns.date"), cell: (e) => e.date },
    { key: "rec", header: t("expenses.columns.recurrent"), cell: (e) => (e.recurrent ? <Badge tone="info"><Repeat className="h-3 w-3" /> {t("expenses.yes")}</Badge> : <span className="text-text3">—</span>) },
    { key: "actions", header: t("common.actions"), align: "right", cell: (e) => <Button variant="ghost" size="xs" className="text-danger" onClick={() => setToDelete(e)}><Trash2 className="h-3 w-3" /></Button> },
  ];

  return (
    <PageContainer>
      <PageHeader
        title={t("expenses.title")}
        subtitle="Coûts opérationnels de l'agence"
        actions={<Button size="sm" onClick={() => setAddOpen(true)}><Plus className="h-3 w-3" /> {t("expenses.add")}</Button>}
      />

      <div className="grid grid-cols-2 md:grid-cols-6 gap-2.5 mb-5">
        <KPICard label="Total mois" value={formatDZD(total)} note={`${formatDZD(recurrent)} récurrent`} />
        <KPICard label="Équipe" value={formatDZD(byCategory("TEAM"))} />
        <KPICard label="Outils" value={formatDZD(byCategory("TOOLS"))} />
        <KPICard label="Loyer" value={formatDZD(byCategory("RENT"))} />
        <KPICard label="Freelance" value={formatDZD(byCategory("FREELANCE"))} />
        <KPICard label="Autre" value={formatDZD(byCategory("OTHER"))} />
      </div>

      {isError && <ApiFallbackBanner onRetry={() => refetch()} />}

      <div className="mb-3.5">
        <SegmentedFilter options={FILTERS} value={filter} onChange={setFilter} />
      </div>

      <Card>
        {isLoading ? (
          <LoadingState rows={6} />
        ) : (
          <DataTable columns={columns} rows={visible} rowKey={(e) => e.id} empty={{ title: "Aucune dépense" }} />
        )}
      </Card>

      <AddExpenseModal open={addOpen} onClose={() => setAddOpen(false)} onSave={onCreate} />

      <ConfirmDialog
        open={!!toDelete}
        onClose={() => setToDelete(undefined)}
        title="Archiver la dépense"
        message={`Archiver « ${toDelete?.description} » ?`}
        confirmLabel="Archiver"
        onConfirm={onArchiveConfirm}
      />
    </PageContainer>
  );
}

function AddExpenseModal({ open, onClose, onSave }: { open: boolean; onClose: () => void; onSave: (input: ExpenseInput) => void }) {
  const { EXPENSE_CATEGORY } = useLabels();
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState<ExpenseCategory>("TEAM");
  const [amount, setAmount] = useState("");
  const [recurrent, setRecurrent] = useState(false);
  const [error, setError] = useState("");

  const submit = () => {
    const value = Number(amount);
    if (!description.trim() || !value || value <= 0) { setError("Champs invalides"); return; }
    onSave({ description: description.trim(), category, amount: value, recurrent });
    setDescription(""); setAmount(""); setError("");
  };

  return (
    <Modal open={open} onClose={onClose} title="Ajouter une dépense"
      footer={<><Button variant="ghost" size="sm" onClick={onClose}>Annuler</Button><Button size="sm" onClick={submit}>Ajouter</Button></>}>
      <Field label="Description" required error={error}>
        <input className={fieldInputClass} value={description} onChange={(e) => setDescription(e.target.value)} />
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Catégorie" required>
          <select className={fieldInputClass} value={category} onChange={(e) => setCategory(e.target.value as ExpenseCategory)}>
            {(Object.keys(EXPENSE_CATEGORY) as ExpenseCategory[]).map((c) => <option key={c} value={c}>{EXPENSE_CATEGORY[c].label}</option>)}
          </select>
        </Field>
        <Field label="Montant (DZD)" required>
          <input type="number" className={fieldInputClass} value={amount} onChange={(e) => setAmount(e.target.value)} />
        </Field>
      </div>
      <label className="flex items-center gap-2 text-[12.5px] text-text2 cursor-pointer">
        <input type="checkbox" checked={recurrent} onChange={(e) => setRecurrent(e.target.checked)} />
        Dépense récurrente
      </label>
    </Modal>
  );
}
